import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { PrepaidPassPublicService, PublicPrepaidPass } from './prepaid-pass-public.service';
import {
  PREPAID_PASS_ERROR_GENERIC,
  PREPAID_PASS_ERROR_INVALID,
  buildPrepaidPassView,
} from './prepaid-pass.util';

export type PrepaidPassStep = 'loading' | 'ready' | 'error';

/**
 * CWS-1835: public prepaid pass opened from the purchase SMS check-in link, so the customer
 * can show (or save) the QR staff scan to redeem. Same URL the purchase email's QR encodes:
 *   /prepaid/check-in?token={redemption token}
 * Layout reuses the public receipt page's sheet and card styles.
 */
@Component({
  selector: 'app-prepaid-pass-page',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './prepaid-pass-page.component.html',
  styleUrls: ['../receipt/receipt-page.component.scss', './prepaid-pass-page.component.scss'],
})
export class PrepaidPassPageComponent implements OnInit, OnDestroy {
  readonly step = signal<PrepaidPassStep>('loading');
  readonly errorMessage = signal('');
  private readonly pass = signal<PublicPrepaidPass | null>(null);
  readonly view = computed(() => buildPrepaidPassView(this.pass()));

  private routeSub?: Subscription;
  private loadSub?: Subscription;
  private lastToken: string | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly prepaidPassPublic: PrepaidPassPublicService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.queryParamMap.subscribe(query => {
      const token = query.get('token')?.trim() || '';
      if (token === this.lastToken) {
        return;
      }
      this.lastToken = token;
      if (token) {
        this.loadPass(token);
      } else {
        // Drop any in-flight load so a late response can't bring back the previous token's pass.
        this.loadSub?.unsubscribe();
        this.showError(PREPAID_PASS_ERROR_INVALID);
      }
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.loadSub?.unsubscribe();
  }

  private loadPass(token: string): void {
    this.loadSub?.unsubscribe();
    this.step.set('loading');
    this.errorMessage.set('');
    this.loadSub = this.prepaidPassPublic.loadPass(token).subscribe({
      next: response => {
        if (!response?.data) {
          this.showError(PREPAID_PASS_ERROR_GENERIC);
          return;
        }
        this.pass.set(response.data);
        this.step.set('ready');
      },
      error: (error: unknown) => {
        // 400 is the validator refusing a malformed token (e.g. a link cut short): invalid too.
        const invalidLink =
          error instanceof HttpErrorResponse && (error.status === 400 || error.status === 404);
        this.showError(invalidLink ? PREPAID_PASS_ERROR_INVALID : PREPAID_PASS_ERROR_GENERIC);
      },
    });
  }

  private showError(message: string): void {
    this.pass.set(null);
    this.step.set('error');
    this.errorMessage.set(message);
  }
}
